import {
  add,
  func,
  mul,
  num,
  pow,
  simplify,
  stringify,
  variable,
  type ExprNode,
  type FuncName,
} from '@learnforge/demo-math/expr';
import { computeNaturalDomain, type Interval } from './domain';
import { evaluate, sampleCurve } from './evaluate';

function naturalIntervalsWithin(node: ExprNode, a: number, b: number): Interval[] {
  const domain = computeNaturalDomain(node);
  const intervals = domain.intervals.flatMap((interval) => {
    const start = Math.max(a, interval.start);
    const end = Math.min(b, interval.end);
    return start < end ? [{ ...interval, start, end }] : [];
  });

  for (const exclusion of [...domain.excluded].sort((left, right) => left.x - right.x)) {
    const index = intervals.findIndex(
      (interval) => exclusion.x > interval.start && exclusion.x < interval.end,
    );
    if (index < 0) continue;
    const containing = intervals[index]!;
    intervals.splice(
      index,
      1,
      { ...containing, end: exclusion.x, openRight: true },
      { ...containing, start: exclusion.x, openLeft: true },
    );
  }

  return intervals;
}

export function isOneToOneNumeric(
  node: ExprNode,
  a: number,
  b: number,
  samples: number = 400,
): boolean {
  const ranges: Array<[number, number]> = [];
  const intervals = naturalIntervalsWithin(node, a, b);
  for (const interval of intervals) {
    const proportion = (interval.end - interval.start) / Math.max(Number.EPSILON, b - a);
    const buf = sampleCurve(
      node,
      interval.start,
      interval.end,
      Math.max(32, Math.round(samples * proportion)),
    );
    let lastDirection: -1 | 0 | 1 = 0;
    let changed = false;
    let previous = Number.NaN;
    let minimum = Infinity;
    let maximum = -Infinity;
    for (const y of buf) {
      if (!Number.isFinite(y)) continue;
      minimum = Math.min(minimum, y);
      maximum = Math.max(maximum, y);
      if (!Number.isFinite(previous)) {
        previous = y;
        continue;
      }
      const direction: -1 | 0 | 1 = y > previous ? 1 : y < previous ? -1 : 0;
      if (direction !== 0 && lastDirection !== 0 && direction !== lastDirection) return false;
      if (direction !== 0) {
        lastDirection = direction;
        changed = true;
      }
      previous = y;
    }
    if (!changed || !Number.isFinite(minimum) || !Number.isFinite(maximum)) return false;
    ranges.push([minimum, maximum]);
  }

  for (let left = 0; left < ranges.length; left += 1) {
    for (let right = left + 1; right < ranges.length; right += 1) {
      const overlapStart = Math.max(ranges[left]![0], ranges[right]![0]);
      const overlapEnd = Math.min(ranges[left]![1], ranges[right]![1]);
      const tolerance = 1e-7 * Math.max(1, Math.abs(overlapStart), Math.abs(overlapEnd));
      if (overlapEnd - overlapStart > tolerance) return false;
    }
  }

  return ranges.length > 0;
}

interface RestrictionEntry {
  pattern: (node: ExprNode) => boolean;
  interval: Interval;
}

const RESTRICTION_TABLE: RestrictionEntry[] = [
  {
    pattern: (n) => stringify(n).replace(/\s+/g, '') === 'x^2',
    interval: { start: 0, end: Infinity, openLeft: false, openRight: true },
  },
  {
    pattern: (n) => /^sin\(x\)$/.test(stringify(n).replace(/\s+/g, '')),
    interval: { start: -Math.PI / 2, end: Math.PI / 2, openLeft: false, openRight: false },
  },
  {
    pattern: (n) => /^cos\(x\)$/.test(stringify(n).replace(/\s+/g, '')),
    interval: { start: 0, end: Math.PI, openLeft: false, openRight: false },
  },
  {
    pattern: (n) => /^tan\(x\)$/.test(stringify(n).replace(/\s+/g, '')),
    interval: { start: -Math.PI / 2, end: Math.PI / 2, openLeft: true, openRight: true },
  },
];

export function suggestRestriction(node: ExprNode): Interval | null {
  if (isOneToOneNumeric(node, -100, 100)) return null;
  for (const entry of RESTRICTION_TABLE) {
    if (entry.pattern(node)) return entry.interval;
  }
  return null;
}

export function formalInverse(node: ExprNode): ExprNode | null {
  const containsX = (candidate: ExprNode): boolean => {
    switch (candidate.kind) {
      case 'num':
        return false;
      case 'var':
        return candidate.name === 'x';
      case 'add':
      case 'mul':
        return candidate.children.some(containsX);
      case 'pow':
        return containsX(candidate.base) || containsX(candidate.exp);
      case 'func':
        return candidate.args.some(containsX);
    }
  };

  const inverseFunction: Partial<Record<FuncName, FuncName>> = {
    exp: 'ln',
    log: 'exp',
    ln: 'exp',
    sqrt: 'pow',
    cbrt: 'pow',
    sin: 'asin',
    cos: 'acos',
    tan: 'atan',
    asin: 'sin',
    acos: 'cos',
    atan: 'tan',
  };

  const solve = (candidate: ExprNode, output: ExprNode): ExprNode | null => {
    if (candidate.kind === 'var') return candidate.name === 'x' ? simplify(output) : null;
    if (candidate.kind === 'num') return null;

    if (candidate.kind === 'add' || candidate.kind === 'mul') {
      const variableChildren = candidate.children.filter(containsX);
      if (variableChildren.length !== 1) return null;
      const constantChildren = candidate.children.filter((child) => !containsX(child));
      const constantNode = candidate.kind === 'add' ? add(constantChildren) : mul(constantChildren);
      const constantValue = evaluate(constantNode, 0);
      if (!Number.isFinite(constantValue)) return null;
      if (candidate.kind === 'mul' && constantValue === 0) return null;
      const nextOutput =
        candidate.kind === 'add'
          ? add([output, num(-constantValue)])
          : mul([output, num(1 / constantValue)]);
      return solve(variableChildren[0]!, simplify(nextOutput));
    }

    if (candidate.kind === 'pow') {
      if (!containsX(candidate.base) || containsX(candidate.exp)) return null;
      const exponent = evaluate(candidate.exp, 0);
      if (!Number.isFinite(exponent) || exponent === 0) return null;
      const root =
        exponent === 2
          ? func('sqrt', [output])
          : exponent === 3
            ? func('cbrt', [output])
            : pow(output, num(1 / exponent));
      return solve(candidate.base, simplify(root));
    }

    if (candidate.args.length !== 1 || !candidate.args[0] || !containsX(candidate.args[0])) {
      return null;
    }
    const inverse = inverseFunction[candidate.name];
    if (!inverse) return null;
    const nextOutput =
      candidate.name === 'sqrt'
        ? pow(output, num(2))
        : candidate.name === 'cbrt'
          ? pow(output, num(3))
          : func(inverse, [output]);
    return solve(candidate.args[0], simplify(nextOutput));
  };

  return solve(node, variable('x'));
}
